import Box, { type BoxProps } from '@mui/material/Box';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import { TOP_BAR_HEIGHT } from '@openx/constants/styles';
import { noop } from '@openx/utils/noop';
import { type MouseEventHandler, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '../Logo/Logo';
import { HamburgerIcon } from '../Navigation/icons';
import { type UserOption, UserPanel } from '../Navigation/UserPanel';
import { SIZES } from '../theme/sizes';
import { HelpMenu, type HelpMenuItem } from './HelpMenu/HelpMenu';

const StyledNavbarWrapperBox = ({ height, ...props }: BoxProps & { height: number }) => (
  <Box {...props} sx={{ height: `${height}px`, gridRow: 1, gridColumn: '1/3' }} />
);

const StyledContainerHeaderBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={{
      alignItems: 'center',
      backgroundColor: 'background.paper',
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      height: '54px',
    }}
  />
);

const StyledContainerLogoBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={{
      alignItems: 'center',
      borderColor: 'divider',
      borderRightStyle: 'solid',
      borderRightWidth: '1px',
      display: 'flex',
      height: '100%',
      width: `${SIZES.sidebarWidth}px`,
      paddingLeft: '32px',
    }}
  />
);

const StyledOrganizationBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      marginLeft: '10px',
      marginRight: '8px',
      maxHeight: '56px',
    }}
  />
);

const StyledOrganizationCaptionBox = (props: BoxProps) => (
  <Box {...props} sx={{ color: 'text.secondary', fontSize: '10px', fontWeight: '400', lineHeight: '16px' }} />
);

const StyledOrganizationNameBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={{ color: 'primary.main', fontSize: '11px', fontWeight: '600', lineHeight: '16px', textTransform: 'uppercase' }}
  />
);

const StyledLogoBox = ({ isOA, children }: { isOA?: boolean; children?: ReactNode }) => {
  const logoWidth = isOA ? 210 : 130;
  return <Box sx={{ alignItems: 'center', display: 'flex', width: `${logoWidth}px` }}>{children}</Box>;
};

const StyledHeaderSeparatorBox = (props: BoxProps) => (
  <Box {...props} sx={{ alignSelf: 'stretch', backgroundColor: 'divider', width: '1px' }} />
);

const StyledHeaderSmallSeparatorBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={{
      alignSelf: 'center',
      backgroundColor: 'divider',
      height: '50%',
      marginLeft: '8px',
      marginRight: '8px',
      width: '1px',
    }}
  />
);

const StyledUserContainerBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={{ display: 'flex', flex: '1', height: '100%', justifyContent: 'flex-end', paddingRight: '32px' }}
  />
);

const StyledHamburgerIconButton = (props: IconButtonProps) => (
  <IconButton
    {...props}
    sx={{
      '&:hover': { backgroundColor: 'transparent' },
      height: 'auto',
      margin: '0.5rem',
      padding: '14px',
      width: 'auto',
    }}
  />
);

export interface NavbarProps {
  userName: string;
  showUserPanel?: boolean;
  onLogoutClick?: () => void;
  onHamburgerClick?: MouseEventHandler;
  searchComponent?: ReactNode;
  helperMenuConfig: HelpMenuItem[];
  isOA?: boolean;
  showHelpIcon?: boolean;
  accountName?: string;
  userPanelSettingsOptions?: UserOption[];
}

/**
 * @deprecated - Please use the {@link AppBar} and the {@link DashboardLayout} instead.
 */
export function Navbar({
  userName,
  showUserPanel = true,
  onLogoutClick = noop,
  onHamburgerClick = noop,
  searchComponent,
  helperMenuConfig,
  isOA,
  showHelpIcon = true,
  accountName,
  userPanelSettingsOptions = [],
}: Readonly<NavbarProps>) {
  const { t } = useTranslation();

  const userPanelProps = useMemo(() => {
    return {
      hideUserName: true,
      options: [
        ...userPanelSettingsOptions,
        {
          action: () => {
            onLogoutClick();
          },
          label: t('Logout'),
          warn: true,
        },
      ],
      userName: userName,
    };
  }, [userName, userPanelSettingsOptions, t, onLogoutClick]);

  return (
    <StyledNavbarWrapperBox height={TOP_BAR_HEIGHT}>
      <StyledContainerHeaderBox>
        <StyledContainerLogoBox>
          <StyledLogoBox isOA={isOA}>
            <Logo data-test="logo" />
          </StyledLogoBox>
        </StyledContainerLogoBox>
        <StyledHamburgerIconButton
          onClick={onHamburgerClick}
          disableRipple
          disableFocusRipple
          data-test="hamburger-menu"
          size="large"
        >
          <HamburgerIcon />
        </StyledHamburgerIconButton>
        <StyledHeaderSeparatorBox />
        {searchComponent && (
          <>
            {searchComponent}
            <StyledHeaderSeparatorBox />
          </>
        )}
        <StyledUserContainerBox>
          {accountName && (
            <>
              <StyledOrganizationBox data-test="navbar-organization-header">
                <StyledOrganizationCaptionBox data-test="navbar-organization-header-title">
                  Organization
                </StyledOrganizationCaptionBox>
                <StyledOrganizationNameBox data-test="navbar-organization-header-name">
                  {accountName}
                </StyledOrganizationNameBox>
              </StyledOrganizationBox>
              <StyledHeaderSmallSeparatorBox />
            </>
          )}
          {showHelpIcon && (
            <>
              <HelpMenu config={helperMenuConfig} />
              <StyledHeaderSmallSeparatorBox />
            </>
          )}
          {showUserPanel && userPanelProps && <UserPanel {...userPanelProps} />}
        </StyledUserContainerBox>
      </StyledContainerHeaderBox>
    </StyledNavbarWrapperBox>
  );
}
